import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { FuseConfigService } from '@fuse/services/config';
import { AppConfig, Scheme } from 'app/core/config/app.config';
import { UserService } from 'app/core/user/user.service';
import { Layout } from 'app/layout/layout.types';
import { User } from 'app/models/user.model';
import { ImageCroppedEvent, LoadedImage } from 'ngx-image-cropper';
import { Subject, takeUntil } from 'rxjs';
import { SettingsService } from './settings.service';
import { SiteService, SitesMap } from 'app/core/sites/site.service';
import { FiltersService } from '../filters/filters.service';

@Component({
    selector     : 'settings',
    templateUrl  : './settings.component.html',
    styleUrls    : ['./settings.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [SettingsService]
})
export class SettingsComponent implements OnInit, OnDestroy {
    /**
     * Objects
     */
    config: AppConfig;
    layout: Layout;
    scheme: 'dark' | 'light';
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    // Options for populating form button selectors
    availableLanguages = [{id: 'el', value: 'Ελληνικά'}, {id: 'en', value: 'English'}];
    availableThemes = [{id: 'dark', value: 'Dark'}, {id: 'light', value: 'Light'}];
    // Default values
    defaultLang = 'en';
    defaultScheme: Scheme = 'light';
    // Local instance of user
    // private siteList: string[] = [];
    private siteList: string[] = [];
    loggedUser: User;
    // loggedUserSiteList: string[] = [];
    loggedUserSiteList: string[] = [];
    // Ngx image cropper objects
    imageChangedEvent: any = '';
    croppedImage: any = '';
    flagCodes = {
        'en': 'uk',
        'el': 'el'
    };

    /**
     * Constructor
     * @param _router
     * @param _fuseConfigService
     * @param _userService
     * @param settingsService
     */
    constructor(private _router: Router, private _fuseConfigService: FuseConfigService, private _userService: UserService, 
        private _filterService: FiltersService, public settingsService: SettingsService, private _siteService: SiteService) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Get all sites in case of admin login
        this.getSites();
        // Subscribe to config changes
        this._fuseConfigService.config$
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((config: AppConfig) => {

            // Store the config
            this.config = config;
        });

        // Get user inforamtion
        this.getUser();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------
   
    /**
     * Function for receiving the new image upload
     */
    fileChangeEvent(event: any): void {
        this.imageChangedEvent = event;
    }

    /**
     * Get cropped image
     * @param event 
     */
    imageCropped(event: ImageCroppedEvent) {
        this.croppedImage = event.base64;
    }

    imageLoaded(image: LoadedImage) {}

    cropperReady() {}

    loadImageFailed() {}

    /**
     * Reset local values and form value
     */
    discardImage(): void {
        this.settingsService.resetForm();
        this.populateForm(this.loggedUser);
        this.imageChangedEvent = '';
        this.croppedImage = '';
        this.config.scheme = this.loggedUser.scheme ? this.loggedUser.scheme : 'light';
        this.defaultLang = this.loggedUser.language ? this.loggedUser.language : 'en';    }

    /**
     * Save user info changes
     */
    onSubmit(): void {
        // this.getNewUser();
        this.settingsService.onSubmit(this.loggedUser, this.config.scheme, this.defaultLang, this.croppedImage)
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe({
            next: (response: any) => {

                // Reload user
                this._userService.user = response.workload;
                this.discardImage();
            },
            error: (er: any) => {
                console.log(er);
            }
        });
    }

    /**
     * Sign out
     */
    signOut(): void {
        this.setScheme('light');
        this.setActiveLang('en');
        this._router.navigate(['/sign-out']);
    }

    /**
     * Set the active lang
     * @param lang
     */
    setActiveLang(lang: string): void {
        // Set the active lang
        this.defaultLang = lang;
        this.settingsService.setActiveLang(lang);
    }

    /**
     * Set the scheme on the config
     *
     * @param scheme
     */
    setScheme(scheme: Scheme): void {
        this.settingsService.setScheme(scheme);
    }

    // -----------------------------------------------------------------------------------------------------
    // Private methods
    // -----------------------------------------------------------------------------------------------------

    private getSites(): void {
        this._siteService.sites$
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe({
            next: (response: SitesMap[]) => {
                this.siteList = response.flatMap(el=>el.codes);
            }, 
            error: er => {
                console.log(er);
            }
        })

    }

    private getUser(): void {
        this._userService.user$
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe({
            next: (user: User) => {
                
                this.loggedUser = user;
                if (this.loggedUser.roles.includes('ROLE_ADMIN') || this.loggedUser.roles.includes('ROLE_GENERAL_MANAGER')) {
                    this.loggedUserSiteList = this.siteList.length > 0 ? this.siteList : [];
                } else {
                    // this.loggedUserSiteList = user.sites ? user.sites.split(',') : [];
                    this.loggedUserSiteList = user.sites_delimited.split(',') ? user.sites_delimited.split(',') : [];
                }
                this.defaultLang = user.language ? user.language : 'en';
                this.defaultScheme = user.scheme ? user.scheme : 'light';
                this.populateForm(user);
                this.setScheme(this.defaultScheme);
            },
            error: (er: any) => {
                console.log(er);
            }
        })
    }

    private populateForm(user: User) {
        this.settingsService.populateForm(user);
    }
}